import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { BindingEnum, BindingSchema } from "../types";

// Constants for server configuration validation
const MIN_URI_LENGTH = 1;
const MIN_TIMEOUT_SECONDS = 1;
const DEFAULT_TIMEOUT_SECONDS = 30;
const DEFAULT_RETRY_ATTEMPTS = 3;
// Client-side sanity bound. The actual deployment cap is enforced by the
// engine's worker_timeout validator and may be tighter (e.g. 60s on cloud).
const MAX_TIMEOUT_SECONDS = 3600;

const timeoutSchema = z.coerce
  .number()
  .int()
  .min(MIN_TIMEOUT_SECONDS, `Timeout must be at least ${MIN_TIMEOUT_SECONDS}s`)
  .max(MAX_TIMEOUT_SECONDS, `Timeout must be ${MAX_TIMEOUT_SECONDS}s or less`)
  .default(DEFAULT_TIMEOUT_SECONDS);

const commonConfig = {
  uri: z
    .string()
    .min(MIN_URI_LENGTH, "URI is required")
    .url("URI must be a valid URL"),
  timeout: timeoutSchema,
  retry: z.coerce.number().default(DEFAULT_RETRY_ATTEMPTS),
};

// Secret metadata schema for response
const secretMetadataSchema = z.object({
  binding: BindingEnum.optional(),
  editable: z.boolean(),
  exists: z.boolean(),
});

// OAuth2 response schema (from API)
const oauth2ResponseSchema = z.object({
  client_id: z.string().optional(),
  client_secret: z.union([z.string(), secretMetadataSchema]).optional(),
  authorization_url: z.union([z.string(), z.string().url()]).optional(),
  external_id: z.string().optional(),
  redirect_uri: z.string().optional(),
  supported_scopes: z.array(z.string()).optional(),
});

// OAuth2 form schema (for submission)
const oauth2FormSchema = z.object({
  client_id: z.string().optional(),
  client_secret: z.string().optional(),
  authorization_url: z
    .union([
      z.string().url("Authorization URL must be a valid URL"),
      z.literal(""),
    ])
    .optional(),
  external_id: z.string().optional(),
  redirect_uri: z.string().optional(),
});

// Secret value response schema (can be string or object with metadata)
const secretValueSchema = z.union([z.string(), secretMetadataSchema]);

const mcpAuthConfigResponse = {
  external_id: z.string().optional(),
  redirect_uri: z.string().optional(),
  oauth2: oauth2ResponseSchema.optional(),
  headers: z.record(z.string(), z.string()).optional(),
  secrets: z.record(z.string(), secretValueSchema).optional(),
};

const serverSchema = z
  .object({
    id: z
      .string()
      .min(1, "ID is required")
      .regex(
        /^[a-z0-9_-]+$/,
        "ID must contain only lowercase letters, numbers, underscores, and hyphens"
      ),
    enabled: z.boolean().default(true),
    binding: BindingSchema,
    type: z.enum(["mcp", "http"]).default("mcp"),
    managed: z.boolean().default(false),
    http: z
      .object({
        ...commonConfig,
        secret: z.object({
          binding: BindingEnum.optional(),
          editable: z.boolean(),
          exists: z.boolean(),
          value: z.string().min(1, "Secret is required"),
        }),
      })
      .optional(),
    mcp: z.object({ ...commonConfig, ...mcpAuthConfigResponse }).optional(),
    requirements: z
      .object({
        met: z.boolean(),
        authorization: z
          .object({
            met: z.boolean(),
            oauth2: z
              .object({
                met: z.boolean(),
              })
              .optional(),
          })
          .optional(),
      })
      .optional(),
  })
  .refine(
    (data) => {
      if (data.type === "http") {
        return !!data.http;
      }
      if (data.type === "mcp") {
        return !!data.mcp;
      }
      return false;
    },
    {
      message: "Server configuration must match the selected type",
      path: ["type"],
    }
  );

export type Server = z.infer<typeof serverSchema>;

/**
 * Helper function to check if a server requires OAuth2 authentication
 */
export const requiresOAuth = (server: Server): boolean => {
  if (
    server.type !== "mcp" ||
    !server.requirements ||
    server.requirements.met
  ) {
    return false;
  }

  return server.requirements.authorization?.oauth2?.met === false;
};

/**
 * Returns the OAuth scopes supported by the server, if discovered.
 */
export const getScopesSupported = (server: Server): string[] | undefined =>
  server.mcp?.oauth2?.supported_scopes;

// Response schema
const ServerResponseSchema = z.object({
  items: z.array(serverSchema),
  limit: z.number(),
  offset: z.number(),
  page_count: z.number(),
  total_count: z.number(),
});
export type ServerResponse = z.infer<typeof ServerResponseSchema>;

// Regex for ID validation (defined at top level for performance)
const ID_REGEX = /^[a-z0-9_-]+$/;

// Create a dynamic schema based on whether we're creating or editing
export const createServerFormSchema = (existingServer?: Server) => {
  const isEditing = !!existingServer;

  // Get all existing secret names from the server (those that exist on the backend)
  const existingSecretNames = new Set<string>();
  if (
    isEditing &&
    existingServer?.type === "mcp" &&
    existingServer.mcp?.secrets
  ) {
    for (const [key, value] of Object.entries(existingServer.mcp.secrets)) {
      if (typeof value === "object" && value.exists) {
        existingSecretNames.add(key);
      }
    }
  }

  const hasExistingHttpSecret =
    isEditing &&
    existingServer?.type === "http" &&
    existingServer?.http?.secret?.exists === true;

  return z
    .object({
      id: z
        .string()
        .min(1, "ID is required")
        .regex(
          ID_REGEX,
          "ID must contain only lowercase letters, numbers, underscores, and hyphens"
        ),
      enabled: z.boolean().default(true),
      binding: BindingSchema,
      type: z.enum(["mcp", "http"]).default("mcp"),
      http: z
        .object({
          uri: z
            .string()
            .min(MIN_URI_LENGTH, "URI is required")
            .url("URI must be a valid URL"),
          secret: hasExistingHttpSecret
            ? z.string().optional().or(z.literal(""))
            : z.string().min(1, "Secret is required"),
          timeout: timeoutSchema,
          retry: z.coerce.number().default(DEFAULT_RETRY_ATTEMPTS),
        })
        .optional(),
      mcp: z
        .object({
          uri: z
            .string()
            .min(MIN_URI_LENGTH, "URI is required")
            .url("URI must be a valid URL"),
          timeout: timeoutSchema,
          retry: z.coerce.number().default(DEFAULT_RETRY_ATTEMPTS),
          oauth2: oauth2FormSchema.optional(),
          headers: z
            .record(z.string(), z.string())
            .optional()
            .refine(
              (headers) => {
                if (!headers) {
                  return true;
                }
                return Object.entries(headers).every(
                  ([key, value]) => !key || value.trim() !== ""
                );
              },
              {
                message: "Header value is required when header key is provided",
              }
            ),
          secrets: z
            .record(z.string(), z.string())
            .optional()
            .refine(
              (secrets) => {
                if (!secrets) {
                  return true;
                }
                // For each secret, check if it's new - if so, require value
                return Object.entries(secrets).every(([key, value]) => {
                  if (!key) {
                    return true;
                  }
                  // If this secret exists in the server, allow empty value
                  if (existingSecretNames.has(key)) {
                    return true;
                  }
                  // New secret must have a value
                  return value.trim() !== "";
                });
              },
              {
                message: "Secret value is required for new secrets",
              }
            ),
        })
        .optional(),
    })
    .refine(
      (data) => {
        if (data.type === "http") {
          return !!data.http;
        }
        if (data.type === "mcp") {
          return !!data.mcp;
        }
        return false;
      },
      {
        message: "Server configuration must match the selected type",
        path: ["type"],
      }
    );
};

export type ServerForm = z.input<ReturnType<typeof createServerFormSchema>>;

export type FieldProps = {
  form: UseFormReturn<ServerForm, unknown>;
  canUpdate: boolean;
};

export type FieldPropsWithType = FieldProps & {
  type: "http" | "mcp";
};

export type ServerHealthResponse = {
  id: string;
  enabled: boolean;
  message: string;
  healthy: boolean;
};

export type DeploymentStatus =
  | "pending"
  | "updating"
  | "unknown"
  | "running"
  | "failed";

export type ServerDeploymentStatusResponse = {
  status: DeploymentStatus;
};

const ServerTestRequestSchema = z
  .object({
    type: z.enum(["http", "mcp"]),
    http: z
      .object({
        uri: z.string(),
      })
      .optional(),
    mcp: z
      .object({
        uri: z.string(),
      })
      .optional(),
  })
  .refine(
    (data) => {
      if (data.type === "http") {
        return !!data.http;
      }
      if (data.type === "mcp") {
        return !!data.mcp;
      }
      return false;
    },
    {
      message: "Server configuration must match the selected type",
      path: ["type"],
    }
  );

export type ServerTestRequest = z.infer<typeof ServerTestRequestSchema>;

const ServerTestResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type ServerTestResponse = z.infer<typeof ServerTestResponseSchema>;

const ServerAuthorizationResponseSchema = z.object({
  worker_id: z.string(),
  user_id: z.string(),
  scopes: z.array(z.string()),
  url: z.string().url(),
  status: z.enum(["pending", "completed", "failed"]),
});

export type ServerAuthorizationResponse = z.infer<
  typeof ServerAuthorizationResponseSchema
>;
